import { calendarDayOf, type IsoDate } from '@platform/design-system';
import type { Operation, ResponseBody, TypedClient } from '@platform/openapi';
import { useKeysetPages } from '../useKeysetPages.ts';
import { queryKeys, usePageContext } from '../usePageContext.ts';

export type HistoryEntry = ResponseBody<Operation<'/admin/history', 'get'>>['items'][number];
export type HistoryKind = NonNullable<HistoryEntry['kind']>;
export type FieldChange = HistoryEntry['changes'][number];
export type FieldValue = FieldChange['before'];

export const AUDIT_READ_ROLE = 'audit:read';

export const HISTORY_KINDS = [
  'people',
  'subsidiaries',
  'products',
  'categories',
  'depots',
  'settings',
  'finance',
  'exports',
] as const satisfies readonly HistoryKind[];

export type HistoryFilters = {
  readonly kind: HistoryKind | null;
  readonly actor: string | null;
  readonly from: IsoDate | null;
  readonly to: IsoDate | null;
};

export const NO_HISTORY_FILTERS: HistoryFilters = { kind: null, actor: null, from: null, to: null };

export const isFiltered = (filters: HistoryFilters): boolean =>
  Object.values(filters).some((value) => value !== null);

const PAGE_SIZE = 50;

const pageQuery = (filters: HistoryFilters, pageToken: string | null) => ({
  page_size: PAGE_SIZE,
  ...(filters.kind === null ? {} : { kinds: filters.kind }),
  actor_id: filters.actor,
  from_day: filters.from,
  to_day: filters.to,
  page_token: pageToken,
});

export const useHistory = (api: TypedClient, filters: HistoryFilters) => {
  const { tenantId } = usePageContext();
  return useKeysetPages<HistoryEntry>({
    queryKey: queryKeys.history(tenantId, filters),
    resource: 'dashboard',
    fetchPage: (pageToken) => api.get('/admin/history', { query: pageQuery(filters, pageToken) }),
  });
};

export type HistoryDay = { readonly day: IsoDate; readonly entries: readonly HistoryEntry[] };

export const groupByDay = (entries: readonly HistoryEntry[], timeZone?: string): readonly HistoryDay[] =>
  entries.reduce<HistoryDay[]>((days, entry) => {
    const day = calendarDayOf(entry.occurredAt, timeZone);
    const last = days.at(-1);
    if (last?.day === day) days[days.length - 1] = { day, entries: [...last.entries, entry] };
    else days.push({ day, entries: [entry] });
    return days;
  }, []);
